import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

export class Drag {
  constructor(
    public event: MouseEvent | TouchEvent | PointerEvent,
    public start: Position,
    public element: HTMLElement,
    public offset: Position,
    public current: Position = start,
  ) {}
}

export interface Position {
  x: number;
  y: number;
}

export const isTouchEvent = (event: Event): event is TouchEvent => event.type.startsWith('touch');

export const pick = <O extends object, K extends keyof O>(obj: O, keys: K[]): Pick<O, K> => keys.reduce((acc, key) => ({
  ...acc,
  [key]: obj[key],
}), {}) as Pick<O, K>;

export const subtractPosition = <T extends Position>(minuend: T, subtrahend: Position): T => ({
  ...minuend,
  x: minuend.x - subtrahend.x,
  y: minuend.y - subtrahend.y,
});

export const moveRectTo = (rect: DOMRect, position: Position): DOMRect => new DOMRect(
  position.x,
  position.y,
  rect.width,
  rect.height,
);

@Directive({
  selector: '[widgetlike-draggable]',
  exportAs: 'widgetlikeDraggable',
  })
export class DraggableDirective implements AfterViewInit, OnDestroy {
  /**
   * CSS selector for element that defines to what position the drag element is constrained
   */
  @Input() constrainer?: string;
  @Input('widgetlike-drag-disabled') disabled?: boolean;
  @Input('widgetlike-drag-property') property: 'position' | 'translate' | 'translate3d' = 'translate3d';
  @Input() handle = 'widgetlike-drag-handle';
  @Output('widgetlike-dragstart') dragstart = new EventEmitter<Drag>();
  @Output('widgetlike-drag') drag = new EventEmitter<Drag>();
  @Output('widgetlike-dragend') dragend = new EventEmitter<Drag>();

  public data: Drag | null = null;

  private constrainerElement: HTMLElement | null = null;

  constructor(
    public elementRef: ElementRef<HTMLElement>,
  ) {}

  public ngAfterViewInit(): void {
    if (this.constrainer) {
      this.constrainerElement = document.querySelector(this.constrainer);
    }

    this.element.addEventListener('pointerdown', this.start);
  }

  public ngOnDestroy(): void {
    this.element.removeEventListener('pointerdown', this.start);
    window.removeEventListener('pointermove', this.move);
    window.removeEventListener('pointerup', this.end);
  }

  public get constrainingElement() {
    const element = this.constrainerElement;

    if (element) {
      return element;
    }

    if (this.constrainer) {
      this.constrainerElement = document.querySelector(this.constrainer);

      return this.constrainerElement;
    }

    return null;
  }

  public get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  private get rect(): DOMRect {
    return this.element.getBoundingClientRect();
  }

  public start = (event: MouseEvent | TouchEvent | PointerEvent) => {
    if (this.disabled !== undefined && this.disabled !== false) {
      return;
    }
    if (!this.checkOrigin(event)) {
      return;
    }

    const pointerPosition = this.getPointerPosition(event);
    const elementRect = this.rect;

    const mouseOffset = subtractPosition(pointerPosition, elementRect);

    this.data = new Drag(
      event,
      pick(elementRect, ['x', 'y']),
      this.element,
      mouseOffset,
    );

    this.dragstart.emit(this.data);

    window.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.end);
  };

  public move = (event: MouseEvent | TouchEvent | PointerEvent): void => {
    if (!this.data) {
      return;
    }

    const pointerPosition = this.getPointerPosition(event);
    const newPosition = subtractPosition(pointerPosition, this.data.offset);

    this.setPosition(newPosition);

    this.drag.emit(new Drag(event, this.data.start, this.data.element, this.data.offset, newPosition));
  };

  public end = () => {
    if (!this.drag) {
      return;
    }

    if (this.data) {
      this.dragend.emit(this.data);
      this.data = null;
    }

    document.body.removeEventListener('pointermove', this.move);
    document.body.removeEventListener('pointerup', this.end);
  };

  public setPosition(position: Position, applyConstraints = true): Position {
    if (applyConstraints) {
      position = this.constrain(moveRectTo(this.rect, position));
    }

    switch (this.property) {
      case 'position': {
        this.element.style.left = position.x + 'px';
        this.element.style.top = position.y + 'px';
        break;
      }
      case 'translate': {
        this.element.style.transform = `translate(${position.x}px, ${position.y}px)`;
        break;
      }
      case 'translate3d': {
        this.element.style.transform = `translate3d(${position.x}px, ${position.y}px, 0px)`;
        break;
      }
    }

    if (this.data) {
      this.data.current = position;
    }

    return position;
  }

  private getPointerPosition(event: MouseEvent | TouchEvent | PointerEvent) {
    if (isTouchEvent(event)) {
      const touch = event.touches[0];

      return { x: touch.clientX, y: touch.clientY };
    }

    return { x: event.clientX, y: event.clientY };
  }

  private constrain(rect: DOMRect): Position {
    const constrainingElement = this.constrainingElement;
    if (!constrainingElement) {
      return pick(rect, ['x', 'y']);
    }

    const constrainBoundingRect = constrainingElement.getBoundingClientRect();

    let x = rect.x, y = rect.y;

    if (rect.right > constrainBoundingRect.right) {
      x = constrainBoundingRect.right - rect.width;
    }

    if (rect.bottom > constrainBoundingRect.bottom) {
      y = constrainBoundingRect.bottom - rect.height;
    }

    return {
      x: Math.max(x, constrainBoundingRect.x),
      y: Math.max(y, constrainBoundingRect.y),
    };
  }

  private checkOrigin(event: Event) {
    const target = event.target as HTMLElement;

    return this.isInHandle(target);
  }

  private isInHandle(element: HTMLElement) {
    if (element.hasAttribute(this.handle)) {
      return true;
    }

    const parent = element.parentElement;
    if (parent) {
      return this.isInHandle(parent);
    }

    return false;
  }
}
